import { all, call, takeEvery, put, select } from 'typed-redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';

import { loadVersions, fetchProduct, retrieveRulesForProduct, translateSku } from 'src/components/skus/saga';
import { deleteLink, getLinksForAsset } from 'src/services/linkClient';
import { getSceneId } from 'src/selectors/pages/editor';
import { getSceneSize } from 'src/selectors/layers';
import {
    alertDeleteSceneVariationFailed,
    alertDeleteSceneVariationSuccess,
    ParseCroppingCoordinatesFailed,
} from 'src/components/alert/slice';
import { fromCroppingToPoints, translateCanvasPoints } from 'src/util/misc';
import { getAllSkuData } from 'src/selectors/skus';
import { logError } from 'src/loggingManager';
import { deleteAsset } from 'src/services/assetsClient';
import { apiErrorMessage } from 'src/services/apiError';
import getLinks from './selectors/getSceneLinks';
import getDataLinks from './selectors/getSceneDataLinks';
import { getLinkData, mapPurposes, realizeScene } from './service';
import getSceneVariation from './selectors/getSceneVariation';
import {
    retrieveVersionsForSku,
    retrieveProductDetails,
    setMCPSkus,
    loadLink,
    setCurrentLink,
    setCreateMode,
    setLinks,
    loadPreviews,
    loadedPreviews,
    setCroppingArea,
    endCropping,
    saveCropping,
    resetCroppingArea,
    deleteSceneVariationSuccess,
    deleteSceneVariation,
    updateConstraint,
    updateSceneVariableRule,
} from './slice';
import { getSdk } from 'src/sdk';

function* getVersionsForASku({ payload }: PayloadAction<string>): Generator<any, void, any> {
    yield* call(loadVersions, { payload, type: 'skus/loadVersions' } as any);
}

function* loadProductDetails({
    payload,
}: PayloadAction<{ sku: string; skuVersion?: number; attributes?: Record<string, unknown> }>): Generator<
    any,
    void,
    any
> {
    if (payload.sku) {
        const mcp: any = yield* call(translateSku as any, payload);
        const payloadToRetrieve = { ...payload };
        if (mcp.platformId !== payload.sku) {
            yield* put(setMCPSkus({ cimSku: mcp.platformId, cimSkuVersion: mcp.platformIdVersion }));
            payloadToRetrieve.sku = mcp.platformId;
            payloadToRetrieve.skuVersion = mcp.platformIdVersion;
        }
        yield* call(fetchProduct, {
            payload: { ...payloadToRetrieve, throwAgain: true },
            type: 'skus/fetchProduct',
        });
        yield* call(retrieveRulesForProduct, {
            payload: payloadToRetrieve,
            type: 'skus/retrieveRulesForProduct',
        });
    }
}

function* loadSceneConfiguration({ payload }: PayloadAction<string | undefined>): Generator<any, void, any> {
    if (payload) {
        const links: any = yield* select(getLinks);
        const link = links.find((l: any) => l.linkId === payload);
        const cacheLinks: any = yield* select(getDataLinks);
        if (link) {
            const cache = cacheLinks.find((l: any) => l.sceneLinkId === link.linkId);
            let data: any;
            if (cache) {
                data = cache;
            } else {
                data = yield* call(getLinkData, link);
            }
            const skus: any = yield* select(getAllSkuData);
            if (!skus[`${data.sku}`]) {
                yield* put(retrieveVersionsForSku(data.sku));
            }
            if (!skus[`${data.sku}-${data.skuVersion}`]) {
                yield* put(retrieveProductDetails({ sku: data.sku, skuVersion: data.skuVersion }));
            }

            yield* put(setCurrentLink(data));
        } else {
            yield* put(loadLink(undefined));
        }
    }
}

function* loadLinksForScene({ payload }: PayloadAction<any>): Generator<any, void, any> {
    if (!payload) {
        const sceneId = yield* select(getSceneId);
        if (sceneId) {
            const links: any = yield* call(getLinksForAsset, getSdk(), { assetId: sceneId });
            const sceneVariationConfigs = links.filter((link: any) => !!link.variableConfigurationId);
            yield* put(setLinks({ links: sceneVariationConfigs }));
        }
    }
}

function* loadPreviewScenes({
    payload,
}: PayloadAction<{ attributes?: Record<string, unknown>; purposes?: unknown[] }>): Generator<any, void, any> {
    try {
        if (payload.attributes && payload.purposes) {
            const { sku, skuVersion, sceneVariableId, fixedAttributesSelection }: any =
                yield* select(getSceneVariation);
            const attributes = Object.assign({}, fixedAttributesSelection, payload.attributes);
            const { width }: any = yield* select(getSceneSize);
            const images: any = yield* call(realizeScene as any, {
                variationId: sceneVariableId,
                attributes,
                purposes: mapPurposes(payload.purposes as any) as any,
                sku,
                skuVersion,
                width,
            });
            yield* put(loadedPreviews({ images }));
        } else {
            yield* put(
                loadedPreviews({
                    images: [],
                    error: 'Parameter Attribute is required and It was not found, please try again.',
                }),
            );
        }
    } catch (e: any) {
        yield* put(loadedPreviews({ images: [], error: e.message }));
    }
}

function* parseCroppingToApp(): Generator<any, void, any> {
    const sceneConfiguration: any = yield* select(getSceneVariation);
    const { cropping, sceneVariables } = sceneConfiguration;

    const { purpose, ruleId } = cropping;
    const foundItem = sceneVariables.find(
        (i: any) => i.purpose === purpose && i.rules.find((r: any) => r.id === ruleId),
    );
    if (foundItem) {
        const rule = foundItem.rules.find((r: any) => r.id === ruleId);
        const rulePoints = fromCroppingToPoints(rule);
        const parsedPoints = translateCanvasPoints({
            points: rulePoints,
            scale: 1,
            offset: { x: 0, y: 0 },
            target: {
                scale: cropping.scale,
                offset: cropping.points[0],
            },
        });

        yield* put(saveCropping(parsedPoints));
    }
}

function* parseCroppingToSave(): Generator<any, void, any> {
    const sceneConfiguration: any = yield* select(getSceneVariation);
    const { cropping, sceneVariables } = sceneConfiguration;

    const { purpose, ruleId } = cropping;
    const foundItem = sceneVariables.find(
        (i: any) => i.purpose === purpose && i.rules.find((r: any) => r.id === ruleId),
    );
    if (foundItem) {
        const rule = foundItem.rules.find((r: any) => r.id === ruleId);
        const rulePoints = fromCroppingToPoints(rule);
        const parsedPoints = translateCanvasPoints({
            points: rulePoints,
            offset: cropping.points[0],
            scale: cropping.scale,
            target: {
                scale: 1,
                offset: { x: 0, y: 0 },
            },
        });

        try {
            yield* put(saveCropping(parsedPoints));
            yield* put(resetCroppingArea());
        } catch (er: any) {
            yield* put(ParseCroppingCoordinatesFailed());
            console.error(er);
        }
    }
}

function* deleteScene({
    payload,
}: PayloadAction<{ sceneVariableId: string; sceneId: string; linkId?: string }>): Generator<any, void, any> {
    const { sceneVariableId, sceneId, linkId } = payload;
    try {
        if (linkId) {
            const sdk = getSdk();
            yield* call(deleteLink, sdk, { assetId: sceneId, linkId });
            yield* call(deleteAsset, sdk, { assetId: sceneVariableId });
            yield* put(alertDeleteSceneVariationSuccess());
            yield* put(deleteSceneVariationSuccess(sceneVariableId));
        }
    } catch (error: any) {
        yield* put(alertDeleteSceneVariationFailed(apiErrorMessage(error)));
        logError(`failed deleting link: ${error}`);
    }
}

function* constraintCropping({ payload }: PayloadAction<{ field: string; value: unknown }>): Generator<any, void, any> {
    const sceneConfiguration: any = yield* select(getSceneVariation);
    const { cropping, sceneVariables } = sceneConfiguration;

    if (payload.field === 'enable' && payload.value === true) {
        const { purpose, ruleId } = cropping;
        const foundItem = sceneVariables.find(
            (i: any) => i.purpose === purpose && i.rules.find((r: any) => r.id === ruleId),
        );
        if (foundItem) {
            const rule = foundItem.rules.find((r: any) => r.id === ruleId);
            const rulePoints = fromCroppingToPoints(rule);
            const constraintFactor = cropping.constraint.width / cropping.constraint.height;
            const height = (rulePoints[1].x - rulePoints[0].x) / constraintFactor;
            const constraintHeight = rulePoints[0].y + height;

            try {
                yield* put(updateSceneVariableRule({ purpose, ruleId, type: 'y1', value: constraintHeight }));
            } catch (er: any) {
                console.error(er);
            }
        }
    }
}

export default function* sceneConfigurationSagas() {
    return yield* all([
        yield* takeEvery(retrieveVersionsForSku as any, getVersionsForASku),
        yield* takeEvery(retrieveProductDetails as any, loadProductDetails),
        yield* takeEvery(loadLink as any, loadSceneConfiguration),
        yield* takeEvery(setCreateMode, loadLinksForScene),
        yield* takeEvery(loadPreviews as any, loadPreviewScenes),
        yield* takeEvery(setCroppingArea, parseCroppingToApp),
        yield* takeEvery(endCropping, parseCroppingToSave),
        yield* takeEvery(deleteSceneVariation as any, deleteScene),
        yield* takeEvery(updateConstraint as any, constraintCropping),
    ]);
}
